import * as React from 'react';
import { Mail, Users, Calendar } from 'lucide-react';
import { ICON_PX } from '../../../ui';
import { formatDateTime } from '../../../../utils/formatters';
import { AISummaryBanner } from './AISummaryBanner';
import { AttachmentList } from './AttachmentList';

function MetaRow({ icon: Icon, label, value, rightAlign }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0 text-[11px]">
      <Icon size={ICON_PX.sm} className="text-text-muted shrink-0" />
      <span className="text-text-muted shrink-0">{label}</span>
      <span className={`font-medium truncate text-text-dark ${rightAlign ? 'ml-auto text-right' : ''}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function ThreadMessage({ msg, isLatest }) {
  const [expanded, setExpanded] = React.useState(isLatest);

  return (
    <div className="rounded-lg border border-nn-border bg-nn-white overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-surface-warm transition-colors"
      >
        <span className="text-[12px] font-semibold text-text-dark truncate flex-1 min-w-0">
          {msg.from.name}
        </span>
        <span className="text-[10px] text-text-muted shrink-0">
          {formatDateTime(msg.sentAt)}
        </span>
        <span className="text-[10px] text-text-muted shrink-0">
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          <div
            className="text-[12px] text-text-dark break-words leading-relaxed max-w-none"
            dangerouslySetInnerHTML={{ __html: msg.bodyHtml || msg.bodyText }}
          />
          {msg.attachments?.length > 0 && (
            <AttachmentList attachments={msg.attachments} />
          )}
        </div>
      )}
    </div>
  );
}

function EmailDetailBody({ email }) {
  if (!email) return null;

  const hasThread = email.threadMessages?.length > 1;

  return (
    <div className="flex flex-col gap-3">
      {/* Metadata — From + Date row, To row, CC row */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <MetaRow icon={Mail} label="From" value={`${email.from.name} <${email.from.email}>`} />
          </div>
          <div className="shrink-0">
            <MetaRow icon={Calendar} label="Date" value={formatDateTime(email.receivedAt)} rightAlign />
          </div>
        </div>
        <MetaRow icon={Users} label="To" value={email.to.map((t) => t.name).join(', ')} />
        {email.cc?.length > 0 && (
          <MetaRow icon={Users} label="CC" value={email.cc.map((c) => c.name).join(', ')} />
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-nn-border" />

      {/* AI Summary */}
      <AISummaryBanner
        summary={email.threadSummary}
        confidence={email.classificationConfidence}
      />

      {/* Thread / Body */}
      {hasThread ? (
        <div className="flex flex-col gap-2">
          {email.threadMessages.map((msg, i) => (
            <ThreadMessage
              key={msg.id}
              msg={msg}
              isLatest={i === 0}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div
            className="text-[12px] text-text-dark break-words leading-relaxed"
            dangerouslySetInnerHTML={{ __html: email.bodyHtml || email.bodyText }}
          />
          {email.attachments?.length > 0 && (
            <AttachmentList attachments={email.attachments} />
          )}
        </div>
      )}

    </div>
  );
}

export { EmailDetailBody };
